import { CommonModule } from '@angular/common';
import { Component, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@app/config/store';
import { email, noSpacesValidator, otp, DtoRegex } from '@app/dto/common.dto';
import { AuthService, LoginResp } from '@app/services/auth.service';
import { RecaptchaService } from '@app/services/recaptcha.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { matRemoveRedEye, matVisibilityOff } from '@ng-icons/material-icons/baseline';
import { matDeleteOutlineOutline } from '@ng-icons/material-icons/outline';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { ClarityService } from '@app/services/clarity.service';
import { config } from '@app/config/app';
import { EncryptionService } from '@app/services/encrytion.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    RouterModule,
    ReactiveFormsModule,
    NgIconComponent,
    CommonModule,
    ChangePasswordComponent,
  ],
  providers: [provideIcons({ matDeleteOutlineOutline, matRemoveRedEye, matVisibilityOff })],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnDestroy {
  showTimer: boolean = false;
  otpExpirationTime: number = 30;
  isMfaUserTemporaryDisable: boolean = false;
  @ViewChild('changePassword') changePasswordRef!: TemplateRef<HTMLElement>;

  redirectURL: string = '';
  passwordFieldType: string = 'password';
  isMfaRegistered: boolean = false;
  changePasswordHelpText: string =
    'Please update your password to continue. This popup will remain open until the update is complete.';

  passwordExpired: boolean = false;
  loginForm: FormGroup = new FormGroup({
    userId: new FormControl('', [email]),
    password: new FormControl('', []),
    token: new FormControl('', []),
    isUserConsent: new FormControl(false),
  });

  mfaOtpValidationForm: FormGroup = new FormGroup({
    userId: new FormControl({ value: '', disabled: true }, []),
    otp: new FormControl('', [noSpacesValidator(), otp]),
  });

  loginResp!: LoginResp;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private captchaInterval: any;

  constructor(
    private router: Router,
    protected store: Store,
    private authSvc: AuthService,
    private recaptcha: RecaptchaService,
    private ngbModal: NgbModal,
    private clarityService: ClarityService,
    private encrypt: EncryptionService,
  ) {
    const redirectUrl = this.router.parseUrl(this.router.url).queryParams['redirectUrl'];

    if (redirectUrl && redirectUrl !== '') sessionStorage.setItem('redirectUrl', redirectUrl);

    this.redirectURL = sessionStorage.getItem('redirectUrl') ?? '';
    sessionStorage.removeItem('redirectUrl');

    this.loginForm.controls['userId'].valueChanges
      .pipe(
        tap(() => {
          if (this.loginForm.controls.isUserConsent.value) {
            this.loginForm.controls.isUserConsent.setValue(false);
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
      )
      .subscribe((val) => {
        this.handleInput(val);
      });

    this.verifyCaptcha();

    // Refresh token every 2 minutes (120,000 ms)
    this.captchaInterval = setInterval(() => {
      this.verifyCaptcha();
    }, 120000);
  }

  ngOnDestroy() {
    if (this.captchaInterval) {
      clearInterval(this.captchaInterval);
    }
  }

  async login(): Promise<void> {
    const payload = this.loginForm.getRawValue();
    payload.password = await this.encrypt.encryptWithPublicKey(payload.password as string);
    payload.userId = await this.encrypt.base64Encode(payload.userId as string);

    this.authSvc.login(payload).subscribe((resp) => {
      if (resp) {
        if (resp.passwordExpired) {
          this.passwordExpired = true;
          return;
        }
        this.loginResp = resp;
        if (config.env === 'prod') {
          this.clarityService.identify(resp.userName);
          this.clarityService.setTag('userId', resp.userName);
        }
        this.isMfaRegistered = resp.isMfaRegistered;
        if ('isSuperAdminPassChanged' in resp && resp.isSuperAdminPassChanged === false) {
          this.store.setTaxpayer(resp);
          this.ngbModal.open(this.changePasswordRef, {
            centered: true,
            size: 'md',
            keyboard: false,
            backdrop: 'static',
          });
        } else if (resp.isMfaEnable && !resp.isDummyUser) {
          this.store.setTaxpayer(resp);
          this.router.navigate(['/config-mfa'], {
            queryParams: { redirectUrl: this.redirectURL, isMfaRegistered: this.isMfaRegistered },
          });
        } else {
          this.store.setTaxpayer(resp);
          const query = this.redirectURL !== '' ? { redirectUrl: this.redirectURL } : {};
          this.router.navigate(['/redirect'], { queryParams: query });
        }
      }
    });
  }

  handleSuperAdminPasswordChange() {
    this.store.setTaxpayer(this.loginResp);
    const query = this.redirectURL !== '' ? { redirectUrl: this.redirectURL } : {};
    this.router.navigate(['/redirect'], { queryParams: query });
    this.dismissModal();
  }

  togglePasswordVisibility() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  async verifyCaptcha(): Promise<boolean> {
    try {
      const token = await this.recaptcha.execute('login');
      if (!token) return false;

      this.loginForm.patchValue({ token });
      return true;
    } catch (error) {
      console.error('Captcha failed', error);
      return false;
    }
  }

  forgetPassword() {
    this.router.navigate(['/forget-password'], {
      queryParams: { email: this.loginForm.value.userId },
    });
  }

  handleInput(userId: string) {
    if (!userId?.trim()) return;

    this.loginForm.controls.isUserConsent.enable();
    const emailRegex = DtoRegex.email;

    if (emailRegex.test(userId)) {
      this.authSvc.getConsentStatus(userId).subscribe((resp) => {
        if (resp && resp.response) {
          const isUserConsent = resp.response?.isUserConsent ?? false;
          this.loginForm.controls.isUserConsent.setValue(isUserConsent);
          if (isUserConsent) this.loginForm.controls.isUserConsent.disable();
        }
      });
    }
  }

  dismissModal() {
    this.ngbModal.dismissAll();
  }
}
